import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import {
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Option } = Select;

interface PettyCashEntryFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
}

const PettyCashEntryForm: React.FC<PettyCashEntryFormProps> = ({ add, edit, view }) => {
  const [loading, setLoading]               = useState("");
  const [form]                              = Form.useForm();
  const [branches, setBranches]: any        = useState([]);
  const [status, setStatus]                 = useState("");
  const { id: petty_cash_entry_id }         = useParams<{ id: string }>();
  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveRemarks,   setDisapproveRemarks]   = useState("");

  const isReadOnly = !!view;

  function clearFields() {
    form.resetFields();
  }

  async function submitForm(data: any) {
    const formattedData: any = {
      account:          data.account,
      department:       data.department,
      branch_id:        data.branch_id,
      transaction_date: data.transaction_date
        ? dayjs(data.transaction_date).format("YYYY-MM-DD")
        : "",
      requested_by: data.requested_by ?? null,
      amount:       data.amount,
      remarks:      data.remarks ?? null,
    };

    const endpoint = edit
      ? `petty_cash_entries/update`
      : `petty_cash_entries/create`;

    if (edit) {
      formattedData.petty_cash_entry_id = petty_cash_entry_id;
    }

    setLoading("submit");
    const response = await postRequest(endpoint, formattedData, true);

    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${edit ? "update" : "create"} petty cash entry.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `petty_cash_entries/change_status`,
      { petty_cash_entry_id, action: "approved" },
      true
    );

    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve petty cash entry."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapproveRemarks.trim()) {
      setErrorMessage("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `petty_cash_entries/change_status`,
      { petty_cash_entry_id, action: "disapproved", reason: disapproveRemarks },
      true
    );

    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove petty cash entry."
      );
    }
  }

  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    const response = await getRequest(`branches/get`, true);
    if (response.success) {
      setBranches(response.data.data);
    }
    setLoading("");
  }

  async function fetchEntry() {
    if (!petty_cash_entry_id) return;

    setLoading("fetch");
    const response = await getRequest(
      `petty_cash_entries/get`,
      { petty_cash_entry_id },
      true
    );

    if (response.success) {
      const entry = response.data.data[0];
      setStatus(entry.status);
      form.setFieldsValue({
        account:          entry.account,
        department:       entry.department,
        branch_id:        entry.branch_id,
        transaction_date: entry.transaction_date
          ? dayjs(entry.transaction_date)
          : null,
        requested_by: entry.requested_by,
        amount:       entry.amount,
        remarks:      entry.remarks,
      });
    }
    setLoading("");
  }

  useEffect(() => {
    fetchBranches();
    if (edit || view) {
      fetchEntry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = add
    ? "Add Petty Cash Entry"
    : edit
    ? "Edit Petty Cash Entry"
    : "View Petty Cash Entry";

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container w-full">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Account or Check"
                      name="account"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Department"
                      name="department"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        disabled={isReadOnly}
                        loading={loading === "fetch"}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {branches.map((branch: any) => (
                          <Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Transaction Date"
                      name="transaction_date"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format={"MMMM D, YYYY"} disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requested By"
                      name="requested_by"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Amount"
                      name="amount"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isReadOnly}
                        onKeyDown={(event) => {
                          const isDot   = event.key === ".";
                          const hasDot  = event.currentTarget.value.includes(".");
                          const allowed = /[0-9.]/;
                          if (!allowed.test(event.key) || (isDot && hasDot)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Footer Buttons */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button
                        type="primary"
                        onClick={() => {
                          navigateTo("/petty-cash-entry");
                          clearFields();
                        }}
                      >
                        Back
                      </Button>
                    </Form.Item>

                    {/* Approve / Disapprove — only on view mode when status is pending */}
                    {view && status === "pending" && (
                      <>
                        <Form.Item>
                          <Button
                            type="primary"
                            danger
                            loading={loading === "disapprove"}
                            onClick={() => { setDisapproveRemarks(""); setShowDisapproveModal(true); }}
                          >
                            Disapprove
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            loading={loading === "approve"}
                            onClick={submitApprove}
                          >
                            Approve
                          </Button>
                        </Form.Item>
                      </>
                    )}

                    {/* Submit / Update — only on add or edit mode */}
                    {!view && (
                      <Form.Item>
                        <Button
                          htmlType="submit"
                          type="primary"
                          loading={loading === "submit"}
                        >
                          {edit ? "Update" : "Submit"}
                        </Button>
                      </Form.Item>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PettyCashEntryForm;